import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, apiPortraitUrl } from "@/auth";

/* ============================================================
 /rapper/:name — Public rapper profile

 Shows portrait, W/L, streak, monthly points, style, recent
 battles and PvP history. If viewer is signed in and not the
 same rapper, offers a CHALLENGE button.
 ============================================================ */

export default function RapperProfile() {
 const { name } = useParams();
 const { user } = useAuth();
 const nav = useNavigate();

 const [data, setData] = useState(null);
 const [notFound, setNotFound] = useState(false);
 const [showChallenge, setShowChallenge] = useState(false);

 useEffect(() => {
 setData(null); setNotFound(false);
 axios.get(`${API}/rapper/${encodeURIComponent(name)}`)
 .then(r => setData(r.data))
 .catch(() => setNotFound(true));
 }, [name]);

 if (notFound) {
 return (
 <div className="max-w-3xl mx-auto px-6 py-24 text-center">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-4"> NOT FOUND</div>
 <h1 className="font-display text-4xl md:text-5xl mb-6">RAPPER NOT FOUND</h1>
 <Link to="/rappers" data-testid="profile-back-to-roster"
 className="inline-block bg-yt-yellow text-black font-display text-xl px-6 py-3 tracking-wider shadow-brutal">
 ← BACK TO ROSTER
 </Link>
 </div>
 );
 }

 if (!data) {
 return <div className="max-w-6xl mx-auto px-6 py-16 font-mono text-yt-muted animate-pulse-red">LOADING...</div>;
 }

 const p = data.profile;
 const canChallenge = user && user.rapper_name !== p.rapper_name;
 const isSelf = user && user.rapper_name === p.rapper_name;

 return (
 <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
 <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
 {/* LEFT — portrait + stats */}
 <div className="md:col-span-1">
 <div className="aspect-[3/4] bg-black border-4 border-yt-yellow shadow-brutal overflow-hidden relative">
 {p.portrait ? (
 <img src={apiPortraitUrl(p.portrait)} alt={p.rapper_name} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-yt-surface font-display text-6xl text-yt-yellow">{(p.name || "??").slice(0, 2).toUpperCase()}</div>
 )}
 {p.is_online && (
 <div className="absolute top-3 right-3 bg-yt-judge text-black text-xs px-3 py-1 font-mono tracking-widest">
 ● ONLINE
 </div>
 )}
 {p.tier === "champion_pass" && (
 <div className="absolute top-3 left-3 bg-yt-yellow text-black text-xs px-3 py-1 font-mono tracking-widest">
 CHAMPION
 </div>
 )}
 </div>

 <div className="mt-4 grid grid-cols-3 gap-2">
 <StatBox label="WINS" value={p.wins} color="text-yt-judge" />
 <StatBox label="LOSSES" value={p.losses} color="text-yt-red" />
 <StatBox label="STREAK" value={p.streak} color="text-yt-yellow" />
 </div>
 <div className="mt-2 grid grid-cols-2 gap-2">
 <StatBox label="WIN RATE" value={`${p.win_rate}%`} color="text-white" />
 <StatBox label="MONTHLY PTS" value={p.monthly_points} color="text-yt-yellow" />
 </div>
 </div>

 {/* RIGHT — meta + action + history */}
 <div className="md:col-span-2">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-1">
 {p.tier === "champion_pass" ? "CHAMPION PASS" : p.tier === "battle_pass" ? "ARENA + CHARTS PASS" : "SPECTATOR"}
 </div>
 <h1 className="font-display text-6xl md:text-8xl tracking-tight mb-2">{p.rapper_name}</h1>
 <div className="font-mono text-lg text-yt-yellow mb-6">{p.style?.toUpperCase()}</div>

 {/* CTAs */}
 <div className="flex flex-wrap gap-3 mb-8">
 {canChallenge && (
 <button data-testid="profile-challenge-btn"
 onClick={() => setShowChallenge(true)}
 className="bg-yt-red text-white font-display text-2xl tracking-wider px-8 py-4 border-4 border-yt-yellow shadow-brutal hover:bg-yt-yellow hover:text-black transition-colors">
 CHALLENGE {p.rapper_name.toUpperCase()}
 </button>
 )}
 {isSelf && (
 <Link to="/inbox" data-testid="profile-my-inbox"
 className="bg-yt-yellow text-black font-display text-xl tracking-wider px-6 py-4 shadow-brutal">
 MY CHALLENGES
 </Link>
 )}
 {!user && (
 <Link to="/register" data-testid="profile-signup-cta"
 className="bg-yt-yellow text-black font-display text-xl tracking-wider px-6 py-4 shadow-brutal">
 SIGN UP TO CHALLENGE
 </Link>
 )}
 <Link to="/rappers" data-testid="profile-back-btn"
 className="border-2 border-white text-white font-mono text-xs tracking-widest px-4 py-4 hover:bg-white hover:text-black transition-colors">
 ← BACK
 </Link>
 </div>

 {/* Recent PvP */}
 {data.recent_pvp?.length > 0 && (
 <div className="mb-8">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-3">RECENT PVP</div>
 <div className="space-y-2">
 {data.recent_pvp.map(c => (
 <Link key={c.id} to={`/pvp/${c.id}`}
 className="block bg-yt-surface border border-yt-border hover:border-yt-yellow p-3 transition-colors">
 <div className="flex justify-between items-center">
 <div className="font-mono text-sm text-white">
 {c.challenger_name} <span className="text-yt-muted">vs</span> {c.challenged_name}
 </div>
 <div className="font-mono text-xs">
 <span className={c.winner_name === p.rapper_name ? "text-yt-judge" : "text-yt-red"}>
 {c.winner_name === p.rapper_name ? "★ WON" : "LOST"}
 </span>
 </div>
 </div>
 <div className="font-mono text-[10px] text-yt-muted tracking-widest mt-1">
 {c.arena_name?.toUpperCase()}
 </div>
 </Link>
 ))}
 </div>
 </div>
 )}

 {/* Recent AI battles */}
 {data.recent_battles?.length > 0 && (
 <div>
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-3">RECENT AI BATTLES</div>
 <div className="space-y-2">
 {data.recent_battles.map(b => (
 <div key={b.id} className="bg-yt-surface border border-yt-border p-3">
 <div className="flex justify-between items-center">
 <div className="font-mono text-sm text-white">
 vs {b.opponent_name}
 </div>
 <div className="font-mono text-xs">
 <span className={b.winner === "user" ? "text-yt-judge" : "text-yt-red"}>
 {b.winner === "user" ? "★ WON" : b.winner === "opponent" ? "LOST" : "DRAW"}
 </span>
 <span className="text-yt-muted ml-2">{b.score_user} - {b.score_opponent}</span>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 </div>

 {showChallenge && (
 <ChallengeModal targetName={p.rapper_name} onClose={() => setShowChallenge(false)}
 onSent={(cid) => { setShowChallenge(false); nav(`/pvp/${cid}`); }} />
 )}
 </div>
 );
}

function StatBox({ label, value, color }) {
 return (
 <div className="bg-yt-surface border border-yt-border p-3">
 <div className={`font-display text-3xl ${color} leading-none`}>{value}</div>
 <div className="font-mono text-[9px] text-yt-muted tracking-widest mt-1">{label}</div>
 </div>
 );
}

function ChallengeModal({ targetName, onClose, onSent }) {
 const [arenas, setArenas] = useState([]);
 const [arenaKey, setArenaKey] = useState(null);
 const [trashTalk, setTrashTalk] = useState("");
 const [verse, setVerse] = useState("");
 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState("");

 useEffect(() => {
 axios.get(`${API}/battles/arenas`).then(r => {
 setArenas(r.data || []);
 if (r.data?.[0]) setArenaKey(r.data[0].key);
 }).catch(() => {});
 }, []);

 const send = async () => {
 setErr("");
 if (verse.trim().length < 20) {
 setErr("Your verse is too short — at least 4 real bars.");
 return;
 }
 setBusy(true);
 try {
 const { data } = await axios.post(`${API}/challenges`, {
 challenged_name: targetName,
 arena_key: arenaKey,
 trash_talk: trashTalk.trim(),
 verse: verse.trim(),
 });
 onSent(data.id);
 } catch (e) {
 setErr(e.response?.data?.detail || "Failed to send challenge.");
 } finally {
 setBusy(false);
 }
 };

 return (
 <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto"
 data-testid="challenge-modal" onClick={onClose}>
 <div className="bg-yt-surface border-4 border-yt-yellow shadow-brutal max-w-2xl w-full my-8 p-6 md:p-8"
 onClick={e => e.stopPropagation()}>
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> SEND CHALLENGE</div>
 <h2 className="font-display text-3xl md:text-4xl mb-4">
 CHALLENGE <span className="text-yt-yellow">{targetName}</span>
 </h2>

 {/* Arena picker */}
 <div className="mb-4">
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">ARENA</div>
 <div className="flex flex-wrap gap-2">
 {arenas.map(a => (
 <button key={a.key} onClick={() => setArenaKey(a.key)}
 data-testid={`challenge-arena-${a.key}`}
 className={`px-3 py-2 text-xs font-mono tracking-widest border-2 transition-colors
 ${arenaKey === a.key ? "border-yt-yellow bg-yt-yellow text-black" : "border-yt-border text-white hover:border-yt-yellow"}`}>
 {a.name?.toUpperCase()}
 </button>
 ))}
 </div>
 </div>

 {/* Trash talk (optional) */}
 <div className="mb-4">
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">
 TRASH TALK (optional · 240 chars max)
 </div>
 <input data-testid="challenge-trash-input"
 type="text" value={trashTalk} maxLength={240}
 onChange={e => setTrashTalk(e.target.value)}
 placeholder="Something quick to set the mood..."
 className="w-full bg-black border-2 border-yt-border focus:border-yt-yellow text-white font-mono p-3" />
 </div>

 {/* Verse */}
 <div className="mb-4">
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">
 YOUR OPENING VERSE ({verse.length}/2500)
 </div>
 <textarea data-testid="challenge-verse-input"
 value={verse} rows={10} maxLength={2500}
 onChange={e => setVerse(e.target.value)}
 placeholder="Drop your bars here. This gets locked in the moment you send. Your opponent will hear it, then respond."
 className="w-full bg-black border-2 border-yt-border focus:border-yt-yellow text-white font-mono p-3 resize-none" />
 </div>

 {err && <div data-testid="challenge-err" className="font-mono text-xs text-yt-red mb-3">{err}</div>}

 <div className="flex gap-3 justify-end">
 <button onClick={onClose} data-testid="challenge-cancel-btn"
 className="border-2 border-white text-white font-mono text-xs tracking-widest px-4 py-3 hover:bg-white hover:text-black transition-colors">
 CANCEL
 </button>
 <button onClick={send} disabled={busy} data-testid="challenge-send-btn"
 className="bg-yt-red text-white font-display text-xl tracking-wider px-6 py-3 border-2 border-yt-yellow shadow-brutal hover:bg-yt-yellow hover:text-black transition-colors disabled:opacity-50">
 {busy ? "SENDING..." : "SEND CHALLENGE"}
 </button>
 </div>
 </div>
 </div>
 );
}
